// Redux
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

// Graphql
import { PaymentEntryType } from "@/api/graphql"

// Types
export type ColumnId =
	| keyof Omit<PaymentEntryType, "supplier_account">
	| "supplier_account"
export type Value = string | number

interface PatchEdit {
	type: "edit"
	approved?: boolean
	data: Partial<Record<ColumnId, Value>>
}
interface PatchUndo {
	type: "undo"
	columnId: ColumnId
	approved?: boolean
}

interface PatchDelete {
	type: "delete"
	approved?: boolean
}
interface PatchClear {
	type: "clear"
	approved?: boolean
}

interface PatchApprove {
	approved: boolean
	type?: typeof PaymentPatchesActionType
	data?: Partial<Record<ColumnId, Value>>
}

type PaymentEntryId = number
type OnPatchCellType =
	| OnPatchEditType
	| OnPatchUndoType
	| OnPatchClearType
	| OnPatchDeleteType
	| OnPatchApproveType

interface OnPatchEditType {
	interestPaymentId: string | undefined
	paymentEntryId: PaymentEntryId
	value?: any
	columnId?: ColumnId
	approved?: boolean
}

interface OnPatchUndoType {
	interestPaymentId: string | undefined
	paymentEntryId: PaymentEntryId
	columnId: ColumnId
	approved?: boolean
}
interface OnPatchClearType {
	interestPaymentId: string | undefined
	paymentEntryId: PaymentEntryId
	approved?: boolean
	columnId?: ColumnId
}
interface OnPatchDeleteType {
	interestPaymentId: string | undefined
	paymentEntryId: PaymentEntryId
	approved?: boolean
	columnId?: ColumnId
}

interface OnPatchApproveType {
	interestPaymentId: string | undefined
	paymentEntryId: PaymentEntryId
	approved: boolean
	value?: any
	columnId?: ColumnId
}

export type Patches =
	| PatchEdit
	| PatchUndo
	| PatchDelete
	| PatchClear
	| PatchApprove

export type PatchList = Record<PaymentEntryId, Patches>
export type OnPatchCell = (args: OnPatchCellType) => void

// What types of actions can be performed on the rows?
export const PaymentPatchesActionType = {
	Edit: "edit",
	Undo: "undo",
	Clear: "clear",
	Delete: "delete",
	Approve: "approve",
} as const

interface PaymentPatchesState {
	patches: Array<{
		interestPaymentId: string | undefined
		changes: Array<{
			paymentEntryId: PaymentEntryId
			type: (typeof PaymentPatchesActionType)[keyof typeof PaymentPatchesActionType]
			data?: Partial<Record<ColumnId, Value>>
			approved?: boolean
		}>
	}>
}

export const initialState: PaymentPatchesState = {
	patches: [],
}

const paymentPatchesSlice = createSlice({
	name: "paymentPatches",
	initialState,
	reducers: {
		handlePatchEdit: (
			state,
			action: PayloadAction<{
				interestPaymentId: string | undefined
				paymentEntryId: number
				columnId?: ColumnId
				value?: any
			}>,
		) => {
			const { interestPaymentId, paymentEntryId, columnId, value } =
				action.payload
			const interestPayment = state.patches.find(
				(p) => p.interestPaymentId === interestPaymentId,
			)

			let colData: Partial<Record<ColumnId, Value>> = {}
			if (interestPayment) {
				const existingChange = interestPayment.changes.find(
					(c) => c.paymentEntryId === paymentEntryId,
				)
				if (existingChange?.type === PaymentPatchesActionType.Edit) {
					colData = { ...existingChange.data }
				}
			}

			if (columnId) {
				colData = {
					...colData,
					[columnId]: value,
				}
			}

			if (interestPayment) {
				const existingChangeIndex = interestPayment.changes.findIndex(
					(c) => c.paymentEntryId === paymentEntryId,
				)
				if (existingChangeIndex !== -1) {
					interestPayment.changes[existingChangeIndex] = {
						paymentEntryId,
						type: PaymentPatchesActionType.Edit,
						data: colData,
						approved: false,
					}
				} else {
					interestPayment.changes.push({
						paymentEntryId,
						type: PaymentPatchesActionType.Edit,
						data: colData,
						approved: false,
					})
				}
			} else {
				state.patches.push({
					interestPaymentId,
					changes: [
						{
							paymentEntryId,
							type: PaymentPatchesActionType.Edit,
							data: colData,
							approved: false,
						},
					],
				})
			}
		},
		handlePatchUndo: (
			state,
			action: PayloadAction<{
				interestPaymentId: string | undefined
				paymentEntryId: number
				columnId: ColumnId
			}>,
		) => {
			const { paymentEntryId, interestPaymentId, columnId } =
				action.payload
			const interestPayment = state.patches.find(
				(p) => p.interestPaymentId === interestPaymentId,
			)

			let colData: Partial<Record<ColumnId, Value>> = {}
			if (interestPayment) {
				const existingChange = interestPayment.changes.find(
					(c) => c.paymentEntryId === paymentEntryId,
				)
				if (existingChange?.type === PaymentPatchesActionType.Edit) {
					colData = { ...existingChange.data }
				}
			}

			if (columnId) {
				const { [columnId]: _, ...rest } = colData
				colData = rest
			}

			if (interestPayment) {
				if (Object.keys(colData).length === 0) {
					interestPayment.changes = interestPayment.changes.filter(
						(c) => c.paymentEntryId !== paymentEntryId,
					)
					if (interestPayment.changes.length === 0) {
						state.patches = state.patches.filter(
							(p) => p.interestPaymentId !== interestPaymentId,
						)
					}
				} else {
					const existingChangeIndex =
						interestPayment.changes.findIndex(
							(c) => c.paymentEntryId === paymentEntryId,
						)
					if (existingChangeIndex !== -1) {
						interestPayment.changes[existingChangeIndex] = {
							paymentEntryId,
							type: PaymentPatchesActionType.Edit,
							data: colData,
							approved: false,
						}
					} else {
						interestPayment.changes.push({
							paymentEntryId,
							type: PaymentPatchesActionType.Edit,
							data: colData,
							approved: false,
						})
					}
				}
			} else {
				state.patches.push({
					interestPaymentId,
					changes: [
						{
							paymentEntryId,
							type: PaymentPatchesActionType.Edit,
							data: colData,
							approved: false,
						},
					],
				})
			}
		},
		handlePatchClear: (
			state,
			action: PayloadAction<{
				interestPaymentId: string | undefined
				paymentEntryId: number
			}>,
		) => {
			const { interestPaymentId, paymentEntryId } = action.payload
			const interestPayment = state.patches.find(
				(p) => p.interestPaymentId === interestPaymentId,
			)

			if (interestPayment) {
				interestPayment.changes = interestPayment.changes.filter(
					(c) => c.paymentEntryId !== paymentEntryId,
				)
				if (interestPayment.changes.length === 0) {
					state.patches = state.patches.filter(
						(p) => p.interestPaymentId !== interestPaymentId,
					)
				}
			}
		},
		handlePatchDelete: (
			state,
			action: PayloadAction<{
				interestPaymentId: string | undefined
				paymentEntryId: number
			}>,
		) => {
			const { interestPaymentId, paymentEntryId } = action.payload
			const interestPayment = state.patches.find(
				(p) => p.interestPaymentId === interestPaymentId,
			)

			if (interestPayment) {
				const existingChangeIndex = interestPayment.changes.findIndex(
					(c) => c.paymentEntryId === paymentEntryId,
				)
				if (existingChangeIndex !== -1) {
					interestPayment.changes[existingChangeIndex] = {
						paymentEntryId,
						type: PaymentPatchesActionType.Delete,
						approved: false,
					}
				} else {
					interestPayment.changes.push({
						paymentEntryId,
						type: PaymentPatchesActionType.Delete,
						approved: false,
					})
				}
			} else {
				state.patches.push({
					interestPaymentId,
					changes: [
						{
							paymentEntryId,
							type: PaymentPatchesActionType.Delete,
							approved: false,
						},
					],
				})
			}
		},
		handlePatchApprove: (
			state,
			action: PayloadAction<{
				interestPaymentId: string | undefined
				paymentEntryId: number
				approved: boolean
			}>,
		) => {
			const { interestPaymentId, paymentEntryId, approved } =
				action.payload
			const interestPayment = state.patches.find(
				(p) => p.interestPaymentId === interestPaymentId,
			)

			if (interestPayment) {
				const existingChangeIndex = interestPayment.changes.findIndex(
					(c) => c.paymentEntryId === paymentEntryId,
				)
				if (existingChangeIndex !== -1) {
					interestPayment.changes[existingChangeIndex] = {
						...interestPayment.changes[existingChangeIndex],
						approved: Boolean(approved),
					}
				}
			}
		},
		clearPatchChangesByInterestPaymentId: (
			state,
			action: PayloadAction<{ interestPaymentId: string | undefined }>,
		) => {
			const { interestPaymentId } = action.payload
			state.patches = state.patches.filter(
				(p) => p.interestPaymentId !== interestPaymentId,
			)
		},
	},
})

// Export actions
export const {
	handlePatchEdit,
	handlePatchUndo,
	handlePatchClear,
	handlePatchDelete,
	handlePatchApprove,
	clearPatchChangesByInterestPaymentId,
} = paymentPatchesSlice.actions

// Base selector
export const selectPaymentPatches = (state: {
	paymentPatches: PaymentPatchesState
}) => state.paymentPatches.patches

// Memoized selector
export const selectPatchesByInterestPaymentId = createSelector(
	[
		selectPaymentPatches,
		(_, interestPaymentId: string | undefined) => interestPaymentId,
	],
	(patches, interestPaymentId) =>
		patches.find((p) => p.interestPaymentId === interestPaymentId)
			?.changes ?? [],
)

// Export reducer
export default paymentPatchesSlice.reducer
